import { readonly, ref } from 'vue';
import defaultHeaderLogo from '@/assets/brand/logo-wt-h.png';
import defaultFooterLogo from '@/assets/brand/devology-logo-square.png';

export type BrandingColors = {
  bg: string;
  surface: string;
  surface_alt: string;
  line: string;
  text: string;
  muted: string;
  accent: string;
  accent_strong: string;
  navy: string;
  navy_2: string;
};

export type RuntimeBranding = {
  company_name: string;
  app_title: string;
  app_tagline: string;
  app_description: string;
  header_logo_url: string;
  footer_logo_url: string;
  logo_alt: string;
  home_url: string;
  support_url: string;
  apply_theme_colors: boolean;
  show_public_marketing_card: boolean;
  show_public_learn_more: boolean;
  colors: BrandingColors;
};

export const BRANDING_COLOR_DEFAULTS: BrandingColors = {
  bg: '#f5f7fb',
  surface: '#ffffff',
  surface_alt: '#eef2ff',
  line: '#e5e9f2',
  text: '#0f172a',
  muted: '#5b6475',
  accent: '#ff3c00',
  accent_strong: '#ff5b1f',
  navy: '#0b1024',
  navy_2: '#141a3a',
};

const defaultBranding: RuntimeBranding = {
  company_name: 'Devology',
  app_title: 'Devology Calendar',
  app_tagline: 'Planuj mniej. Zrob wiecej.',
  app_description: 'Kalendarz Devology ulatwia znalezienie terminu spotkania.',
  header_logo_url: defaultHeaderLogo,
  footer_logo_url: defaultFooterLogo,
  logo_alt: 'Devology',
  home_url: 'https://devology.pl',
  support_url: '',
  apply_theme_colors: false,
  show_public_marketing_card: false,
  show_public_learn_more: false,
  colors: { ...BRANDING_COLOR_DEFAULTS },
};

const branding = ref<RuntimeBranding>({ ...defaultBranding, colors: { ...defaultBranding.colors } });

const getColorMap = (payload: RuntimeBranding): Record<string, string> => ({
    '--brand-bg': payload.colors.bg,
    '--brand-surface': payload.colors.surface,
    '--brand-surface-alt': payload.colors.surface_alt,
    '--brand-line': payload.colors.line,
    '--brand-text': payload.colors.text,
    '--brand-muted': payload.colors.muted,
    '--brand-accent': payload.colors.accent,
    '--brand-accent-strong': payload.colors.accent_strong,
    '--brand-navy': payload.colors.navy,
    '--brand-navy-2': payload.colors.navy_2,
    '--colour-neutral-base': payload.colors.surface,
    '--colour-neutral-lower': payload.colors.bg,
    '--colour-neutral-lower-light': payload.colors.bg,
    '--colour-neutral-lower-dark': payload.colors.navy,
    '--colour-neutral-border': payload.colors.line,
    '--colour-primary-soft': payload.colors.surface_alt,
    '--colour-primary-default': payload.colors.accent,
    '--colour-primary-hover': payload.colors.accent_strong,
    '--colour-primary-pressed': payload.colors.accent_strong,
    '--colour-secondary-soft': payload.colors.surface_alt,
    '--colour-secondary-default': payload.colors.navy_2,
    '--colour-secondary-hover': payload.colors.navy,
    '--colour-secondary-pressed': payload.colors.navy,
    '--colour-ti-base': payload.colors.text,
    '--colour-ti-base-light': payload.colors.text,
    '--colour-ti-secondary': payload.colors.muted,
    '--colour-ti-secondary-light': payload.colors.muted,
    '--colour-ti-muted': payload.colors.muted,
    '--colour-ti-highlight': payload.colors.accent,
    '--colour-accent-teal': payload.colors.navy_2,
    '--colour-accent-blue': payload.colors.navy,
    '--colour-accent-purple': payload.colors.navy_2,
    '--colour-accent-orange': payload.colors.accent,
    '--colour-accent-pink': payload.colors.accent_strong,
    '--colour-accent-gray': payload.colors.muted,
    '--colour-apmt-soft': payload.colors.surface_alt,
    '--colour-apmt-primary': payload.colors.accent,
    '--colour-apmt-primary-hover': payload.colors.accent_strong,
    '--colour-apmt-primary-pressed': payload.colors.accent_strong,
    '--colour-apmt-secondary': payload.colors.navy_2,
    '--colour-apmt-accent-1': payload.colors.navy,
    '--colour-apmt-accent-2': payload.colors.accent,
  });

const clearBrandingCss = () => {
  const root = document.documentElement;
  const keys = Object.keys(getColorMap(defaultBranding));
  keys.forEach((name) => root.style.removeProperty(name));
};

const applyBrandingCss = (payload: RuntimeBranding) => {
  if (!payload.apply_theme_colors) {
    clearBrandingCss();
    return;
  }
  const root = document.documentElement;
  const colorMap = getColorMap(payload);
  Object.entries(colorMap).forEach(([name, value]) => root.style.setProperty(name, value));
};

export const setRuntimeBranding = (incoming: Partial<RuntimeBranding> | null | undefined) => {
  const next: RuntimeBranding = {
    ...defaultBranding,
    ...(incoming ?? {}),
    colors: {
      ...defaultBranding.colors,
      ...(incoming?.colors ?? {}),
    },
  };
  if (!next.header_logo_url || next.header_logo_url.startsWith('/src/assets/')) {
    next.header_logo_url = defaultHeaderLogo;
  }
  if (!next.footer_logo_url || next.footer_logo_url.startsWith('/src/assets/')) {
    next.footer_logo_url = defaultFooterLogo;
  }
  branding.value = next;
  applyBrandingCss(next);
};

export const useRuntimeBranding = () => ({
  branding: readonly(branding),
});
